<template>
  <div :class="getClassName('cart-item')">
    <div :class="getClassName('cart-item__counter')">{{ index + 1 }}</div>
    <a :href="'/items/' + value.item.category.slug + '/' + value.item.id">
      <v-img
        :class="getClassName('cart-item__thumbnail')"
        :src="value.color.recommend_thumbnail"
        contain
        :width="media ? '80' : '140'"
        :height="media ? '80' : '140'"
      >
        <template v-slot:placeholder>
          <ImagePlaceholder />
        </template>
      </v-img>
    </a>

    <div :class="getClassName('cart-item__content')">
      <div>
        <div :class="getClassName('cart-item__option')" v-if="value.item.category">
          {{ value.item.category.name }}
        </div>
        <h2 :class="getClassName('cart-item__name')">
          <a :href="'/items/' + value.item.category.slug + '/' + value.item.id">
            {{ value.item.name }}
          </a>
        </h2>
        <div :class="getClassName('cart-item__option')" v-if="value.item.colors.length > 1">
          カラー：{{ value.color.name }}
        </div>
        <div :class="getClassName('cart-item__option')" v-if="value.item.sizes.length > 1">
          サイズ：{{ value.size.name }}
        </div>
      </div>
      <div :class="getClassName('cart-item__value')">
        <div :class="getClassName('cart-item__price')">
          <span :class="getClassName('cart-item__number')">{{ getNumberFormat(value.price) }}</span>
          円(税込)
        </div>
        <div :class="getClassName('cart-item__quantity')">
          <v-text-field
            :class="getClassName('cart-item__field')"
            type="number"
            min="1"
            v-model="internalValue"
            outlined
            hide-details
            dense
          />
          <span :class="getClassName('cart-item__unit')">{{ value.item.unit }}</span>
        </div>
      </div>
      <button :class="getClassName('cart-item__remove')" type="button" @click="destroy()">削除</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { APP_URL, API_URL } from '../../../config/const';
import { OK, CREATED, UNPROCESSABLE_ENTITY, ERROR_MESSAGES, numberFormat } from '../../../_modules/util';

import getClassName from '../../_mixins/getClassName';

import ImagePlaceholder from '../../_components/ImagePlaceholder';

const ENABLE_GA = process.env.MIX_ENABLE_GA === 'true';

export default {
  mixins: [getClassName],
  components: {
    ImagePlaceholder,
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => {},
    },
    index: {
      type: Number,
      required: true,
      default: '',
    },
    media: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  computed: {
    internalValue: {
      get() {
        return this.value.quantity;
      },
      set(newValue) {
        if (this.value.quantity !== newValue) this.$emit('input', { ...this.value, quantity: newValue });
      },
    },
    getNumberFormat() {
      return (number) => numberFormat(number);
    },
  },
  watch: {
    'value.quantity': {
      handler(newValue, oldValue) {
        this.update(newValue, oldValue);
      },
    },
  },
  methods: {
    async update(newValue, oldValue) {
      const response = await axios.put(API_URL + '/cart/' + this.value.id, this.value);

      if (response.status === OK) {
        this.sendTransaction(newValue - oldValue);
        //this.$emit('input', response.data);
        return false;
      }

      if (response.status === UNPROCESSABLE_ENTITY) {
        this.$emit('alert', {
          varient: 'error',
          messages: Object.keys(response.data.errors).flatMap((key) => response.data.errors[key]),
        });
        return false;
      }

      this.$emit('alert', {
        varient: 'error',
        messages: [ERROR_MESSAGES[response.status]],
      });
    },
    async destroy() {
      if (!confirm('この商品をショッピングカートから削除をします。\nよろしいですか？')) return false;

      const response = await axios.delete(API_URL + '/cart/' + this.value.id);

      if (response.status === OK) {
        this.sendTransaction(-1 * this.value.quantity);
        this.$emit('remove', response.data);
        return false;
      }

      this.$emit('alert', {
        varient: 'error',
        messages: [ERROR_MESSAGES[response.status]],
      });
    },
    sendTransaction(quantity = this.value.quantity) {
      const action = quantity <= 0 ? 'remove_from_cart' : 'add_to_cart';
      quantity = quantity < 0 ? -1 * quantity : quantity;
      if (!ENABLE_GA) return;

      gtag('event', action, {
        items: [
          {
            item_id: this.value.option.jan_code,
            item_name: `${this.value.item.category.name} ${this.value.item.name}（カラー：${this.value.color.name}、サイズ：${this.value.size.name}）`,
            item_category: this.value.item.category.name,
            item_variant: `カラー：${this.value.color.name}、サイズ：${this.value.size.name}`,
            index: 1,
            quantity: quantity,
            price: this.value.price,
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../_sass/variables';

.cart-item {
  padding: 20px;
  display: flex;
  align-items: center;
  &:not(:first-child) {
    border-top: solid 1px $color__line;
  }
  &--sp {
    padding: 15px 0 20px;
    align-items: flex-start;
  }
  &__counter {
    font-size: 1.4rem;
    color: #505050;
    &--sp {
      font-size: 1.3rem;
      margin: auto 0;
    }
  }
  &__thumbnail {
    margin-left: 10px;
    flex: 0 0 auto;
  }
  &__content {
    margin-left: 25px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    &--sp {
      margin-left: 10px;
      display: block;
    }
  }

  &__name {
    font-size: 1.6rem;
    font-weight: normal;
    line-height: 1;
    &--sp {
      font-size: 1.4rem;
    }
  }
  &__option {
    color: #505050;
    font-size: 1.3rem;
    line-height: 1.53846;
    &:first-of-type {
      margin-bottom: 10px;
    }
    &:nth-of-type(2) {
      margin-top: 10px;
    }
    &--sp {
      line-height: 1.461538;
    }
  }

  &__value {
    margin-left: auto;
    flex: 0 0 320px;
    display: flex;
    justify-content: center;
    align-items: center;
    &--sp {
      margin-top: 10px;
      flex-flow: row wrap;
      justify-content: flex-end;
    }
  }
  &__price {
    font-size: 1.3rem;
    text-align: right;
    flex: 0 0 130px;
    &--sp {
      font-size: 1.2rem;
      text-align: left;
      flex: 1 0 auto;
    }
  }
  &__number {
    font-size: 1.8rem;
    &--sp {
      font-size: 1.7rem;
    }
  }
  &__quantity {
    margin-left: 60px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    &--sp {
      margin-left: 10px;
      flex: 0 0 auto;
    }
  }
  &__field {
    width: 80px;
    border-color: $color__line;
    border-radius: 0px;
    flex: 0 0 auto;
    &--sp {
      width: 70px;
    }
  }
  &__unit {
    margin-left: 0.5em;
  }
  &__remove {
    background: #f2f2f2;
    width: 56px;
    height: 28px;
    border: solid 1px $color__line;
    border-radius: 3px;
    margin-left: 50px;
    font-family: 'NotoSansJP-Light', sans-serif;
    letter-spacing: 0.5em;
    text-indent: 0.5em;
    line-height: 1;
    justify-self: flex-end;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &--sp {
      margin-top: 15px;
      margin-left: auto;
      font-family: 'NotoSansJP-Regular', sans-serif;
    }
  }
}
</style>
